/** Build-time refinement for the nonlinear surface where rivers actually join.
 * Shared edge splits propagate into neighboring triangles and across chunks;
 * no new draw calls or per-frame terrain queries are introduced. */
import * as THREE from 'three';

type Vertex={point:THREE.Vector3;attributes:Record<string,number[]>};
type Face={vertices:[number,number,number];mesh:number};
export interface RiverSurfaceProbe {radius:number;joining:boolean;ceiling?:number}
export function refineRiverJunctions(
 meshes:readonly THREE.Mesh[],
 probe:(point:THREE.Vector3)=>RiverSurfaceProbe|null,
 update:(vertex:Vertex)=>void,
):void {
 if(!meshes.length)return;
 const vertices:Vertex[]=[],faces:Face[]=[],ids=new Map<string,number>();
 const layout=Object.entries(meshes[0].geometry.attributes).filter(([name])=>name!=='position').map(([name,attribute])=>[name,attribute.itemSize]as const);
 for(let m=0;m<meshes.length;m++){
  const mesh=meshes[m],g=mesh.geometry,p=g.getAttribute('position'),keys=g.userData.riverVertexKeys as string[],local:number[]=[];
  for(let i=0;i<p.count;i++){
   let index=ids.get(keys[i]);
   if(index===undefined){
    index=vertices.length;ids.set(keys[i],index);
    const attributes:Record<string,number[]>={};
    for(const [name,size]of layout){const attribute=g.getAttribute(name);attributes[name]=Array.from({length:size},(_,c)=>attribute.array[i*size+c]);}
    vertices.push({point:new THREE.Vector3().fromBufferAttribute(p,i).add(mesh.position),attributes});
   }
   local.push(index);
  }
  const index=g.index!;for(let i=0;i<index.count;i+=3)faces.push({vertices:[local[index.getX(i)],local[index.getX(i+1)],local[index.getX(i+2)]],mesh:m});
 }
 const edgeKey=(a:number,b:number)=>a<b?`${a}:${b}`:`${b}:${a}`;
 const midpoints=new Map<string,{vertex:Vertex;probe:RiverSurfaceProbe|null;error:number;index?:number}>();
 const midpoint=(a:number,b:number)=>{
  const key=edgeKey(a,b);let result=midpoints.get(key);if(result)return result;
  const va=vertices[a],vb=vertices[b],point=va.point.clone().add(vb.point).multiplyScalar(.5),sample=probe(point),error=sample?Math.abs(point.length()-sample.radius):0;
  const attributes:Record<string,number[]>={};for(const [name,size]of layout)attributes[name]=Array.from({length:size},(_,i)=>(va.attributes[name][i]+vb.attributes[name][i])*.5);
  result={vertex:{point,attributes},probe:sample,error};midpoints.set(key,result);return result;
 };
 const maxTriangles=faces.length*3;let reachedBudget=false;
 let triangles=faces;
 for(let pass=0;pass<6;pass++){
  const split=new Set<string>();
  for(const {vertices:[a,b,c]}of triangles){
   const edges=[[a,b],[b,c],[c,a]]as const;
   let refine=false;
   for(const [x,y]of edges){const m=midpoint(x,y);if(m.probe?.joining&&(m.error>.012||m.vertex.point.length()>(m.probe.ceiling??Infinity)+.00005)){split.add(edgeKey(x,y));refine=true;}}
   const center=vertices[a].point.clone().add(vertices[b].point).add(vertices[c].point).multiplyScalar(1/3),sample=probe(center);
   if(sample?.joining&&(Math.abs(center.length()-sample.radius)>.012||center.length()>(sample.ceiling??Infinity)+.00005))refine=true;
   if(refine)for(const [x,y]of edges)split.add(edgeKey(x,y));
  }
  if(!split.size)break;
  // Each split edge adds one triangle for each incident face, including
  // propagated neighbors. Enforce a global 3x cap before allocating children.
  const incidence=new Map<string,number>();
  for(const {vertices:[a,b,c]}of triangles)for(const [x,y]of[[a,b],[b,c],[c,a]]){const key=edgeKey(x,y);if(split.has(key))incidence.set(key,(incidence.get(key)??0)+1);}
  let remaining=maxTriangles-triangles.length;
  const order=[...split].sort((a,b)=>(midpoints.get(b)?.error??0)-(midpoints.get(a)?.error??0));
  split.clear();for(const key of order){const cost=incidence.get(key)!;if(cost<=remaining){split.add(key);remaining-=cost;}else reachedBudget=true;}
  if(!split.size)break;
  const edgeVertex=(a:number,b:number):number=>{
   if(!split.has(edgeKey(a,b)))return-1;
   const m=midpoint(a,b);if(m.index===undefined){update(m.vertex);m.index=vertices.length;vertices.push(m.vertex);}return m.index;
  };
  const next:Face[]=[];
  for(const face of triangles){
   const [a,b,c]=face.vertices,m=edgeVertex(a,b),n=edgeVertex(b,c),o=edgeVertex(c,a);
   const add=(x:number,y:number,z:number)=>next.push({vertices:[x,y,z],mesh:face.mesh});
   if(m<0&&n<0&&o<0)add(a,b,c);
   else if(n<0&&o<0){add(a,m,c);add(m,b,c);}
   else if(m<0&&o<0){add(b,n,a);add(n,c,a);}
   else if(m<0&&n<0){add(c,o,b);add(o,a,b);}
   else if(o<0){add(b,n,m);add(a,m,c);add(m,n,c);}
   else if(m<0){add(c,o,n);add(b,n,a);add(n,o,a);}
   else if(n<0){add(a,m,o);add(c,o,b);add(o,m,b);}
   else{add(a,m,o);add(m,b,n);add(o,n,c);add(m,n,o);}
  }
  triangles=next;
 }
 for(let m=0;m<meshes.length;m++){
  const mesh=meshes[m],mapping=new Map<number,number>(),indices:number[]=[],positions:number[]=[],attributes:Record<string,number[]>={};
  for(const [name]of layout)attributes[name]=[];
  for(const face of triangles)if(face.mesh===m)for(const id of face.vertices){
   let local=mapping.get(id);
   if(local===undefined){local=mapping.size;mapping.set(id,local);const v=vertices[id];positions.push(v.point.x-mesh.position.x,v.point.y-mesh.position.y,v.point.z-mesh.position.z);for(const [name]of layout)attributes[name].push(...v.attributes[name]);}
   indices.push(local);
  }
  const geometry=mesh.geometry;geometry.setAttribute('position',new THREE.Float32BufferAttribute(positions,3));
  for(const [name,size]of layout)geometry.setAttribute(name,new THREE.Float32BufferAttribute(attributes[name],size));
  geometry.setIndex(indices);geometry.computeBoundingSphere();delete geometry.userData.riverVertexKeys;geometry.userData.riverRefinementCapped=reachedBudget;
 }
}
